import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Card, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFSwitch } from 'src/components/hook-form';
import { SettingType, SystemConfigKey } from 'src/type/settings';
import { toastError } from 'src/utils/format-error';
import * as Yup from 'yup';
import { enqueueSnackbar } from 'notistack';
import UpdatedText from 'src/components/ui-kit/updated-text';
import { useBoolean } from 'src/hooks/use-boolean';

import { useState, useEffect } from 'react';
import LoadingContainer from 'src/components/ui-kit/loading-container';
import axios, { endpoints } from 'src/utils/axios';
import { getSettingValue, getSettingUpdated } from '../../utils';
import SettingWaitingPrice from './setting-waiting-price';
import SettingLv0Balance from './setting-lv0-balance';

const Schema = Yup.object().shape({
  [SystemConfigKey.ONLY_INVITED_USER_CAN_REGISTER]: Yup.boolean(),
  [SystemConfigKey.REDEEM_ENABLED]: Yup.boolean(),
  [SystemConfigKey.TOPUP_ENABLED]: Yup.boolean(),
  [SystemConfigKey.WITHDRAW_ENABLED]: Yup.boolean(),
});

const inputs = [
  {
    title: 'เฉพาะคนที่มี รหัสเชิญเท่านั้นที่จะสมัครได้',
    description:
      'การสมัครสมาชิกจะเปิดให้สมัครเฉพาะผู้ที่มีรหัสเชิญเท่านั้น หากผู้สมัครไม่มีรหัสเชิญจะไม่สามารถสมัครได้ โดยท่านสามารถสร้างรหัสเชิญได้ที่เมนู รหัสเชิญ และส่งให้กับผู้สมัคร',
    name: SystemConfigKey.ONLY_INVITED_USER_CAN_REGISTER,
  },
  {
    title: 'เปิดใช้งานรับทองจริง',
    description: 'หากเปิด ฟีเจอร์นี้ ผู้ใช้งานจะสามารถใช้งานเมนูรับทองจริงได้',
    name: SystemConfigKey.REDEEM_ENABLED,
  },

  {
    title: 'เปิดให้ผู้ใช้งานสามารถเติมเงินได้',
    description: 'หากเปิด ฟีเจอร์นี้ ผู้ใช้งานจะสามารถเติมเงินเข้าระบบได้',
    name: SystemConfigKey.TOPUP_ENABLED,
  },
  {
    title: 'เปิดให้ผู้ใช้งานสามารถถอนเงินได้',
    description: 'หากเปิด ฟีเจอร์นี้ ผู้ใช้งานจะสามารถถอนเงินออกจากระบบได้',
    name: SystemConfigKey.WITHDRAW_ENABLED,
  },
];

export default function SettingSystem() {
  const reload = useBoolean();
  const initialize = useBoolean(true);
  const [settings, setSettings] = useState(Array<SettingType>());

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues: {
      [SystemConfigKey.ONLY_INVITED_USER_CAN_REGISTER]: false,
      [SystemConfigKey.REDEEM_ENABLED]: false,
      [SystemConfigKey.TOPUP_ENABLED]: false,
      [SystemConfigKey.WITHDRAW_ENABLED]: false,
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    load();
  }, [reload.value]);

  const load = async () => {
    try {
      const { data } = await axios.get<SettingType[]>(endpoints.settings.get);
      // console.log(data);

      setSettings(data);
      reset({
        [SystemConfigKey.ONLY_INVITED_USER_CAN_REGISTER]:
          getSettingValue<boolean>(data, SystemConfigKey.ONLY_INVITED_USER_CAN_REGISTER) || false,
        [SystemConfigKey.REDEEM_ENABLED]:
          getSettingValue<boolean>(data, SystemConfigKey.REDEEM_ENABLED) || false,
        [SystemConfigKey.TOPUP_ENABLED]:
          getSettingValue<boolean>(data, SystemConfigKey.TOPUP_ENABLED) || false,
        [SystemConfigKey.WITHDRAW_ENABLED]:
          getSettingValue<boolean>(data, SystemConfigKey.WITHDRAW_ENABLED) || false,
      });
    } catch (error) {
      toastError(error);
    } finally {
      initialize.onFalse();
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    try {
      const body = Object.entries(values).map(([key, value]) => ({ key, value }));

      await axios.post(endpoints.settings.createOrUpdate, { configs: body });
      enqueueSnackbar('บันทึกสำเร็จ');
      reload.onToggle();
    } catch (error) {
      toastError(error);
    }
  });

  return (
    <>
      <LoadingContainer loading={initialize.value} sx={{ minHeight: '30vh' }} fitContent>
        <FormProvider methods={methods} onSubmit={onSubmit}>
          <Card sx={{ py: 5, px: 3 }}>
            <Stack spacing={3}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="h6">เกี่ยวกับระบบ</Typography>
                <Typography variant="caption" color="text.secondary">
                  <UpdatedText {...getSettingUpdated(settings, SystemConfigKey.WITHDRAW_ENABLED)} />
                </Typography>
              </Stack>

              <Stack spacing={2}>
                {inputs.map((input) => (
                  <RHFSwitch
                    key={input.name}
                    name={input.name}
                    label={
                      <Stack>
                        <Typography variant="body2">{input.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {input.description}
                        </Typography>
                      </Stack>
                    }
                  />
                ))}
              </Stack>

              <Box sx={{ mt: 2 }}>
                <LoadingButton
                  variant="contained"
                  type="submit"
                  loading={isSubmitting}
                  sx={{ minWidth: 120 }}
                >
                  บันทึก
                </LoadingButton>
              </Box>
            </Stack>
          </Card>
        </FormProvider>
      </LoadingContainer>

      <SettingWaitingPrice />

      <SettingLv0Balance />
    </>
  );
}
